import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, InputGroup, Spinner } from 'react-bootstrap';
import { z } from 'zod';
import { Eye, EyeSlash } from 'react-bootstrap-icons';

import { ClientSelectGroup } from '@/components/ClientSelectGroup';
import { SecretValue } from '@/components/config/SecretValue';
import { ToolPage } from '@/components/ui/ToolPage';
import { clientAccess } from '@/services/clientAccess';
import { clientService } from '@/services/clientService';
import type { Client } from '@/types';

const passwordResetSchema = z.object({
  clientName: z.string().min(1, 'Choose a client'),
  username: z.string().email('Enter a valid email address'),
  password: z.string().min(8, 'Use at least 8 characters').max(256, 'Use 256 characters or fewer'),
  isTemporary: z.boolean().default(true),
});

/** What the last reset did, kept so the page can say it back and offer the password to copy. */
interface ResetDone {
  clientName: string;
  username: string;
  password: string;
  temporary: boolean;
}

export default function ResetClientPasswordTool() {
  const [clients, setClients] = useState<Client[]>([]);
  const [clientName, setClientName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isTemporary, setIsTemporary] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ResetDone | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    (async () => {
      try {
        setClients(await clientService.getAllClients());
      } catch (e) {
        const detail = e instanceof Error ? ` ${e.message}` : '';
        setError(`Could not load the client list. Reload the page to try again.${detail}`);
      }
    })();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setDone(null);
    setValidationErrors({});

    const formData = {
      clientName,
      username,
      password,
      isTemporary,
    };

    const parsed = passwordResetSchema.safeParse(formData);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const err of parsed.error.errors) {
        errs[String(err.path[0])] = err.message;
      }
      setValidationErrors(errs);
      setIsSubmitting(false);
      return;
    }

    try {
      // The client access Lambda finds the client's account and user pool from its config.
      await clientAccess('users.resetPassword', {
        clientName: parsed.data.clientName.trim(),
        username: parsed.data.username,
        password: parsed.data.password,
        permanent: !parsed.data.isTemporary,
      });
      setDone({
        clientName: parsed.data.clientName,
        username: parsed.data.username,
        password: parsed.data.password,
        temporary: parsed.data.isTemporary,
      });
    } catch (err: unknown) {
      console.error(err);
      const detail = err instanceof Error ? ` ${err.message}` : '';
      setError(`The password was not changed.${detail}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const who = username.trim() || 'the person';
  const where = clientName || 'the client';

  return (
    <ToolPage
      title="Reset a client password"
      subtitle="Set a new password for a person in a client's workspace, with no email step."
      width="narrow"
    >
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <div className="tool-form">
            {/* The client picker sits outside the form: Enter in its search box must never reset a password. */}
            <section className="tool-form__section">
              <h2 className="section-label">Client</h2>
              <ClientSelectGroup value={clientName} onChange={setClientName} clients={clients} />
              {validationErrors.clientName && (
                <div className="invalid-feedback d-block">{validationErrors.clientName}</div>
              )}
            </section>

            <Form className="tool-form" onSubmit={onSubmit} noValidate>
              <section className="tool-form__section">
                <h2 className="section-label">Person and password</h2>
                <Form.Group className="mb-3" controlId="reset-username">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="person@client.com"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    isInvalid={!!validationErrors.username}
                    autoComplete="off"
                  />
                  <Form.Control.Feedback type="invalid">{validationErrors.username}</Form.Control.Feedback>
                  <Form.Text>The email the person signs in to the workspace with.</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="reset-password">
                  <Form.Label>New password</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      className="mono"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      isInvalid={!!validationErrors.password}
                      autoComplete="new-password"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? (
                        <EyeSlash className="me-1" aria-hidden="true" />
                      ) : (
                        <Eye className="me-1" aria-hidden="true" />
                      )}
                      {showPassword ? 'Hide' : 'Show'}
                    </Button>
                    <Form.Control.Feedback type="invalid">{validationErrors.password}</Form.Control.Feedback>
                  </InputGroup>
                  <Form.Text>
                    At least 8 characters. The workspace can ask for more, and the error says what it wants.
                  </Form.Text>
                </Form.Group>

                <Form.Group controlId="reset-temporary">
                  <Form.Check
                    type="checkbox"
                    label="Temporary password"
                    checked={isTemporary}
                    onChange={(e) => setIsTemporary(e.target.checked)}
                  />
                  <Form.Text>
                    The person must choose their own password the next time they sign in. Clear the box to make this
                    password permanent.
                  </Form.Text>
                </Form.Group>
              </section>

              <div className="tool-effect">
                This sets a new password for <strong>{who}</strong> in <strong>{where}</strong>. Their old password
                stops working at once. No email goes to them, so you give them the new password yourself.
              </div>

              <div className="tool-form__actions">
                <Button type="submit" variant="primary" disabled={isSubmitting || !clientName}>
                  {isSubmitting ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Setting the password
                    </>
                  ) : (
                    'Reset password'
                  )}
                </Button>
                {!clientName && <span className="tool-form__hint">Choose a client first.</span>}
              </div>
            </Form>
          </div>
        </Card.Body>
      </Card>

      {done && (
        <Card className="mt-3">
          <Card.Body>
            <h2 className="section-label">Password set</h2>
            <p>
              <strong>{done.username}</strong> in <strong>{done.clientName}</strong> now signs in with the password
              below.{' '}
              {done.temporary
                ? 'They must choose their own the next time they sign in.'
                : 'It stays their password until they change it.'}
            </p>
            <SecretValue label="Password" value={done.password} secret />
            <p className="secret-value__note">
              No email went to them. The password is hidden until you press Show, and it stays on this page until you
              leave it or reset another.
            </p>
          </Card.Body>
        </Card>
      )}
    </ToolPage>
  );
}
